import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config'; 
import { 
    PlusCircle, 
    AlertTriangle, 
    Package, 
    Search,
    TrendingDown,
    IndianRupee,
    Trash2,
    Edit,
    Check,
    X,
    Camera,
    ImageIcon
} from 'lucide-react';

const InventoryComponent = () => {
    const [inventory, setInventory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedImage, setSelectedImage] = useState(null); 
    
    const [editingId, setEditingId] = useState(null);
    const [editFormData, setEditFormData] = useState({});

    const [formData, setFormData] = useState({
        itemName: '',
        category: 'Fabric',
        unit: 'Meters',
        currentStock: '',
        pricePerUnit: '',
        lowStockThreshold: 5,
        note: 'Initial Stock',
        photo: '' 
    });

    useEffect(() => {
        fetchInventory();
    }, []);

    const fetchInventory = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/inventory`);
            setInventory(res.data);
            setLoading(false);
        } catch (err) {
            console.error("Error fetching inventory", err);
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setFormData({ ...formData, photo: reader.result });
            }
            reader.readAsDataURL(file);
        }
    };

    const handleEditFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setEditFormData({ ...editFormData, photo: reader.result });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleEditChange = (e) => {
        setEditFormData({ ...editFormData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...formData,
                photo: formData.photo, 
                currentStock: Number(formData.currentStock),
                pricePerUnit: Number(formData.pricePerUnit),
                lowStockThreshold: Number(formData.lowStockThreshold),
                logs: [{
                    type: 'Added',
                    quantity: Number(formData.currentStock),
                    note: formData.note || "Initial Stock"
                }]
            };
            await axios.post(`${API_BASE_URL}/inventory`, payload);
            alert("Item added successfully!");
            setFormData({ 
                itemName: '', category: 'Fabric', unit: 'Meters', 
                currentStock: '', pricePerUnit: '', lowStockThreshold: 5, 
                note: '', photo: ''
            });
            fetchInventory();
        } catch (err) {
            console.error(err);
            alert("Error adding item: " + (err.response?.data?.message || err.message));
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this item?")) {
            try {
                await axios.delete(`${API_BASE_URL}/inventory/${id}`);
                alert("Item deleted successfully");
                fetchInventory();
            } catch (err) {
                console.error("Error deleting item", err);
                alert("Failed to delete item");
            }
        }
    };

    const startEdit = (item) => {
        setEditingId(item._id);
        // Corrected to use 'photo' to match your schema
        setEditFormData({ ...item, photo: item.photo || '' });
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditFormData({});
    };

    const handleUpdate = async (id) => {
        try {
            const payload = {
                currentStock: Number(editFormData.currentStock),
                photo: editFormData.photo, 
                logs: [
                    ...(editFormData.logs || []),
                    {
                        type: 'Adjustment',
                        quantity: Number(editFormData.currentStock),
                        note: "Updated via Inventory Manager"
                    }
                ]
            };
            
            await axios.put(`${API_BASE_URL}/inventoryupdate/${id}`, payload);
            
            alert("Item updated successfully!");
            setEditingId(null);
            fetchInventory();
        } catch (err) {
            console.error(err);
            alert("Error updating item: " + (err.response?.data?.message || err.message));
        }
    };

    const filteredInventory = inventory.filter(item => 
        item.itemName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.category.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) return <div className="p-5 text-center">Loading Inventory...</div>;

    return (
        <div style={{ padding: '20px', backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
            
            {/* Image Popup Modal */}
            {selectedImage && (
                <div style={modalOverlayStyle} onClick={() => setSelectedImage(null)}>
                    <div style={modalContentStyle} onClick={e => e.stopPropagation()}>
                        <button style={closeButtonStyle} onClick={() => setSelectedImage(null)}><X size={24} /></button>
                        <img src={selectedImage} alt="Full view" style={{ maxWidth: '100%', maxHeight: '65vh', borderRadius: '8px' }} />
                    </div>
                </div>
            )}

            {/* Header Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
                <div style={statCardStyle}>
                    <Package size={20} color="#4f46e5" />
                    <div>
                        <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>Total Items</p>
                        <h3 style={{ margin: 0 }}>{inventory.length}</h3>
                    </div>
                </div>
                <div style={statCardStyle}>
                    <AlertTriangle size={20} color="#ef4444" />
                    <div>
                        <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>Low Stock Alert</p>
                        <h3 style={{ margin: 0 }}>{inventory.filter(i => i.currentStock <= i.lowStockThreshold).length}</h3>
                    </div>
                </div>
                <div style={statCardStyle}>
                    <IndianRupee size={20} color="#10b981" />
                    <div>
                        <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>Inv. Value</p>
                        <h3 style={{ margin: 0 }}>
                            ₹{inventory.reduce((acc, curr) => acc + (curr.currentStock * curr.pricePerUnit), 0).toLocaleString()}
                        </h3>
                    </div>
                </div>
            </div>

            <div style={{ display: 'flex', gap: '25px', flexWrap: 'wrap' }}>
                {/* Left Side: Entry Form */}
                <div style={{ flex: '1', minWidth: '320px', backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', height: 'fit-content' }}>
                    <h3 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: '10px', fontSize: '1.2rem' }}>
                        <PlusCircle size={20} /> Add New Stock
                    </h3>
                    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        
                        <div>
                            <label style={labelStyle}>Item Photo</label>
                            <div style={photoUploadContainer}>
                                {formData.photo ? (
                                    <div style={{ position: 'relative' }}>
                                        <img src={formData.photo} alt="Preview" style={previewImageStyle} />
                                        <button type="button" onClick={() => setFormData({...formData, photo: ''})} style={removePhotoStyle}><X size={12} /></button>
                                    </div>
                                ) : (
                                    <label style={photoPlaceholderStyle}>
                                        <Camera size={24} color="#999" />
                                        <span style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>Upload</span>
                                        <input type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} />
                                    </label>
                                )}
                            </div>
                        </div>

                        <div>
                            <label style={labelStyle}>Item Name</label>
                            <input name="itemName" value={formData.itemName} onChange={handleInputChange} placeholder="e.g. Pure Silk" style={inputStyle} required />
                        </div>
                        
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                            <div>
                                <label style={labelStyle}>Category</label>
                                <select name="category" value={formData.category} onChange={handleInputChange} style={inputStyle}>
                                    <option>Fabric</option>
                                    <option>Laces</option>
                                    <option>Buttons</option>
                                    <option>Packing Material</option>
                                    <option>Other</option>
                                </select>
                            </div>
                            <div>
                                <label style={labelStyle}>Unit</label>
                                <select name="unit" value={formData.unit} onChange={handleInputChange} style={inputStyle}>
                                    <option>Meters</option>
                                    <option>Pieces</option>
                                    <option>Rolls</option>
                                    <option>Packets</option>
                                    <option>kg</option>
                                    <option>number</option>
                                </select>
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                            <div>
                                <label style={labelStyle}>Quantity</label>
                                <input name="currentStock" type="number" value={formData.currentStock} onChange={handleInputChange} style={inputStyle} required />
                            </div>
                            <div>
                                <label style={labelStyle}>Price/Unit</label>
                                <input name="pricePerUnit" type="number" value={formData.pricePerUnit} onChange={handleInputChange} style={inputStyle} required />
                            </div>
                        </div>

                        <div>
                            <label style={labelStyle}>Low Stock Warning</label>
                            <input name="lowStockThreshold" type="number" value={formData.lowStockThreshold} onChange={handleInputChange} style={inputStyle} />
                        </div>

                        <button type="submit" style={buttonStyle}>Add to Inventory</button>
                    </form>
                </div>

                {/* Right Side: Inventory List */}
                <div style={{ flex: '2', minWidth: '500px' }}>
                    <div style={{ marginBottom: '15px', position: 'relative' }}>
                        <Search style={{ position: 'absolute', left: '10px', top: '10px', color: '#999' }} size={18} />
                        <input 
                            type="text" 
                            placeholder="Search by name or category..." 
                            style={{ ...inputStyle, paddingLeft: '40px' }}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                            <thead style={{ backgroundColor: '#f3f4f6' }}>
                                <tr>
                                    <th style={thStyle}>Photo</th>
                                    <th style={thStyle}>Item</th>
                                    <th style={thStyle}>Category</th>
                                    <th style={thStyle}>Stock</th>
                                    <th style={thStyle}>Rate</th>
                                    <th style={thStyle}>Status</th>
                                    <th style={thStyle}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredInventory.map((item) => (
                                    <tr key={item._id} style={{ borderBottom: '1px solid #eee' }}>
                                        {editingId === item._id ? (
                                            <>
                                                <td style={tdStyle}>
                                                    <div style={{ position: 'relative', width: '40px', height: '40px' }}>
                                                        <input type="file" id={`edit-file-${item._id}`} style={{ display: 'none' }} onChange={handleEditFileChange} />
                                                        <img 
                                                            src={editFormData.photo || ""} 
                                                            alt="edit" 
                                                            style={{ width: '40px', height: '40px', borderRadius: '4px', cursor: 'pointer', objectFit: 'cover', backgroundColor: '#eee' }}
                                                            onClick={() => document.getElementById(`edit-file-${item._id}`).click()}
                                                        />
                                                    </div>
                                                </td>
                                                <td style={tdStyle}>
                                                    <input name="itemName" value={editFormData.itemName} disabled style={{...smallInputStyle, backgroundColor: '#f0f0f0'}} />
                                                </td>
                                                <td style={tdStyle}>
                                                    <span style={badgeStyle}>{item.category}</span>
                                                </td>
                                                <td style={tdStyle}>
                                                    <input name="currentStock" type="number" value={editFormData.currentStock} onChange={handleEditChange} style={{...smallInputStyle, width: '60px'}} />
                                                </td>
                                                <td style={tdStyle}>
                                                    ₹{item.pricePerUnit}
                                                </td>
                                                <td style={tdStyle}>
                                                   --
                                                </td>
                                                <td style={tdStyle}>
                                                    <div style={{ display: 'flex', gap: '8px' }}>
                                                        <button onClick={() => handleUpdate(item._id)} style={actionButtonStyle} title="Save"><Check size={18} color="#10b981" /></button>
                                                        <button onClick={cancelEdit} style={actionButtonStyle} title="Cancel"><X size={18} color="#999" /></button>
                                                    </div>
                                                </td>
                                            </>
                                        ) : (
                                            <>
                                                <td style={tdStyle}>
                                                    {/* Changed item.image to item.photo to match your DB */}
                                                    {item.photo ? (
                                                        <img 
                                                            src={item.photo} 
                                                            alt={item.itemName} 
                                                            style={thumbnailStyle} 
                                                            onClick={() => setSelectedImage(item.photo)}
                                                        />
                                                    ) : (
                                                        <div style={thumbnailPlaceholderStyle}>
                                                            <ImageIcon size={16} color="#ccc" />
                                                        </div>
                                                    )}
                                                </td>
                                                <td style={tdStyle}>
                                                    <div style={{ fontWeight: '600' }}>{item.itemName}</div>
                                                    <div style={{ fontSize: '11px', color: '#999' }}>Added: {new Date(item.dateAdded).toLocaleDateString()}</div>
                                                </td>
                                                <td style={tdStyle}><span style={badgeStyle}>{item.category}</span></td>
                                                <td style={tdStyle}><span style={{ fontWeight: 'bold' }}>{item.currentStock}</span> {item.unit}</td>
                                                <td style={tdStyle}>₹{item.pricePerUnit}</td>
                                                <td style={tdStyle}>
                                                    {item.currentStock <= item.lowStockThreshold ? (
                                                        <span style={{ color: '#ef4444', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 'bold' }}>
                                                            <TrendingDown size={14} /> Refill
                                                        </span>
                                                    ) : (
                                                        <span style={{ color: '#10b981', fontSize: '12px' }}>In Stock</span>
                                                    )}
                                                </td>
                                                <td style={tdStyle}>
                                                    <div style={{ display: 'flex', gap: '10px' }}>
                                                        <button onClick={() => startEdit(item)} style={actionButtonStyle} title="Edit"><Edit size={18} color="#4f46e5" /></button>
                                                        <button onClick={() => handleDelete(item._id)} style={actionButtonStyle} title="Delete"><Trash2 size={18} color="#ef4444" /></button>
                                                    </div>
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {filteredInventory.length === 0 && <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>No items found.</div>}
                    </div>
                </div>
            </div>
        </div>
    );
};

// Styles (No changes made to design as requested)
const statCardStyle = { backgroundColor: 'white', padding: '20px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '15px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' };
const inputStyle = { width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box' };
const smallInputStyle = { padding: '5px', borderRadius: '4px', border: '1px solid #4f46e5', fontSize: '13px', width: '100%' };
const labelStyle = { display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '5px', color: '#555' };
const buttonStyle = { backgroundColor: '#4f46e5', color: 'white', padding: '12px', borderRadius: '6px', border: 'none', fontWeight: '600', cursor: 'pointer', marginTop: '10px' };
const thStyle = { padding: '15px', fontSize: '13px', color: '#666', fontWeight: '600' };
const tdStyle = { padding: '15px', fontSize: '14px' };
const actionButtonStyle = { border: 'none', background: 'none', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center' };
const badgeStyle = { backgroundColor: '#eef2ff', color: '#4f46e5', padding: '4px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' };
const photoUploadContainer = { display: 'flex', gap: '10px', alignItems: 'center' };
const previewImageStyle = { width: '60px', height: '60px', borderRadius: '8px', objectFit: 'cover', border: '1px solid #ddd' };
const photoPlaceholderStyle = { width: '60px', height: '60px', borderRadius: '8px', border: '2px dashed #ddd', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' };
const removePhotoStyle = { position: 'absolute', top: '-5px', right: '-5px', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '50%', width: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' };
const thumbnailStyle = { width: '40px', height: '40px', borderRadius: '6px', objectFit: 'cover', cursor: 'pointer', border: '1px solid #eee' };
const thumbnailPlaceholderStyle = { width: '40px', height: '40px', borderRadius: '6px', backgroundColor: '#f9f9f9', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #eee' };
const modalOverlayStyle = { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalContentStyle = { position: 'relative', maxWidth: '90%', maxHeight: '90%', textAlign: 'center' };
const closeButtonStyle = { position: 'absolute', top: '-40px', right: '0', background: 'none', border: 'none', color: 'white', cursor: 'pointer' };

export default InventoryComponent;
